import { useState, useEffect, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";

let Font = Quill.import("formats/font");
Font.whitelist = [
    "georgia",
    "arial",
    "verdana",
    "courier-new",
    "impact",
    "times-new-roman",
    "comic-sans-ms",
    "papyrus",
    "source-code-pro"
];
Quill.register(Font, true);

var Size = Quill.import("attributors/style/size");
Size.whitelist = [
    "8px",
    "10px",
    "12px",
    "14px",
    "16px",
    "20px",
    "24px",
    "32px",
    "48px",
    "64px"
];
Quill.register(Size, true);

const formats = [
    "font",
    "size",
    "color",
    "background",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "align"
];

// Protocol whitelist for sanitization
const protocolWhitelist = ["http", "https", "mailto", "tel"];

// Custom link sanitization logic
const Link = Quill.import("formats/link");

Link.sanitize = function (url) {
    const protocol = url.split(":")[0];

    // Allow URLs with whitelisted protocols and relative URLs
    if (protocolWhitelist.includes(protocol) || url.startsWith("/")) {
        return url;
    }

    // Otherwise prepend https://
    return `https://${url}`;
};

Quill.register(Link, true);

const TextEditor = (props) => {
    const {
        setGroup,
        realContent,
        isOpen,
        skin,
        highlightColors
    } = props;

    const [content, setContent] = useState("");
    const [tooltipPosition, setTooltipPosition] = useState({
        left: 0,
        top: 0
    });
    const [prevTCPos, setPrevTCPos] = useState(null);
    const quillRef = useRef(null);

    const standardFont = skin.textFont
        .toLowerCase()
        .replace(/ +/g, "-");

    const fontArr = [
        standardFont,
        "times-new-roman",
        "georgia",
        "arial",
        "verdana",
        "courier-new",
        "source-code-pro",
        "impact",
        "comic-sans-ms",
        "papyrus"
    ];

    const standardSize = skin.textSize;

    const fontSizeArr = [
        standardSize,
        "8px",
        "10px",
        "12px",
        "14px",
        "16px",
        "20px",
        "24px",
        "32px",
        "48px",
        "64px"
    ];

    const colorsArr = JSON.parse(
        highlightColors.replace(/'/g, '"')
    );

    const modules = {
        toolbar: [
            [{ font: fontArr }, { size: fontSizeArr }],
            [{ color: colorsArr }, { background: colorsArr }],
            ["bold", "italic", "underline", "strike"],
            ["blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            [
                { align: "" },
                { align: "center" },
                { align: "right" },
                { align: "justify" }
            ],
            [{ indent: "-1" }, { indent: "+1" }],
            ["link"],
            ["clean"]
        ],
        clipboard: {
            matchVisual: false
        }
    };

useEffect(() => {
    if (!isOpen) {
        setContent("");
        setPrevTCPos(null);

        setGroup(prevGroup => ({
            ...prevGroup,
            blockContent: ""
        }));

        return;
    }

    setContent(realContent || "");
}, [isOpen, realContent, setGroup]);

    useEffect(() => {
        if (!isOpen || !quillRef.current) {
            return;
        }

        const quill = quillRef.current.getEditor();

        if (!quill) {
            return;
        }

        const updateTooltipPosition = (range) => {
            if (!range || range.length === 0) {
                return;
            }

            const bounds = quill.getBounds(range.index);
            const editorContainer =
                quillRef.current?.editor?.container;

            if (!editorContainer) {
                return;
            }

            const containerBounds =
                editorContainer.getBoundingClientRect();

            let left =
                containerBounds.left +
                bounds.left -
                160;

            let top =
                containerBounds.top +
                bounds.top +
                bounds.height +
                10;

            const tooltipWidth = 320;
            const tooltipHeight = 40;
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;

            if (left < 0) {
                left = 0;
            } else if (left + tooltipWidth > screenWidth) {
                left = screenWidth - tooltipWidth;
            }

            if (top + tooltipHeight > screenHeight) {
                top = screenHeight - tooltipHeight;
            }

            setTooltipPosition({
                left,
                top
            });
        };

        const adjustTooltipToSelection = () => {
            const tooltip =
                document.querySelector(".ql-tooltip");

            const range = quill.getSelection(true);

            if (!tooltip || !range) {
                return;
            }

            if (range.length === 0) {
                const [leaf] = quill.getLeaf(range.index);

                if (
                    leaf &&
                    leaf.parent &&
                    leaf.parent.formats &&
                    leaf.parent.formats.link
                ) {
                    tooltip.style.left = "";
                    tooltip.style.top = "";
                    tooltip.style.position = "";
                    return;
                }
            }

            tooltip.style.left = `${tooltipPosition.left}px`;
            tooltip.style.top = `${tooltipPosition.top}px`;
            tooltip.style.position = "fixed";
        };

        const handleSelectionChange = (range) => {
            if (!range) {
                return;
            }

            if (
                range.index === prevTCPos &&
                range.length === 0
            ) {
                return;
            }

            updateTooltipPosition(range);
            adjustTooltipToSelection();

            setPrevTCPos(range.index);
        };

        quill.on(
            "selection-change",
            handleSelectionChange
        );

        const linkButton =
            document.querySelector(".ql-link");

        if (linkButton) {
            linkButton.addEventListener(
                "click",
                adjustTooltipToSelection
            );
        }

        return () => {
            quill.off(
                "selection-change",
                handleSelectionChange
            );

            if (linkButton) {
                linkButton.removeEventListener(
                    "click",
                    adjustTooltipToSelection
                );
            }
        };
    }, [isOpen, prevTCPos, tooltipPosition]);

    return (
        <div>
            <ReactQuill
    id="text-editor"
    ref={quillRef}
    theme="snow"
    modules={modules}
    formats={formats}
    value={content}
    onChange={(newContent) => {
        setContent(newContent);

        setGroup(prevGroup => ({
            ...prevGroup,
            blockContent: newContent
        }));
    }}
    placeholder="block body"
/>
        </div>
    );
};

export default TextEditor;